import { Link } from "react-router-dom";
import { LOGO_URL, SERVICES } from "@/lib/rn3";
import { useT } from "@/lib/i18n";
import { ArrowRight, Calendar, FileText, Upload, Sparkles, Globe2, ShieldCheck } from "lucide-react";
import { SocialIcons } from "@/components/Layout";
import PwaInstall from "@/components/PwaInstall";
import SEO from "@/components/SEO";

const Home = () => {
  const t = useT();
  return (
    <div>
      <SEO
        title="Roman Numerals III — Bookkeeping, Tax Prep, Notary & ITIN in Jacksonville, FL"
        description="Bilingual (English/Spanish) bookkeeping, tax preparation, notary, ITIN applications, sales tax reporting, and Spanish translations in Jacksonville, Florida. Solutions. Strategy. Structure. Success."
        path="/"
        breadcrumbs={[{ name: "Home", path: "/" }]}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [
            { "@type": "Question", "name": "Do you serve clients in Spanish and English?", "acceptedAnswer": { "@type": "Answer", "text": "Yes. Roman Numerals III is fully bilingual — every service, document, and conversation is available in both English and Spanish. Our owner Adelis Roman is a native bilingual speaker." } },
            { "@type": "Question", "name": "Where is Roman Numerals III located?", "acceptedAnswer": { "@type": "Answer", "text": "Roman Numerals III is based at 8512 Tower Falls Drive, Jacksonville, Florida. We serve clients throughout Duval County and the State of Florida, and offer remote service for clients across the U.S." } },
            { "@type": "Question", "name": "What services do you offer?", "acceptedAnswer": { "@type": "Answer", "text": "Bookkeeping, tax preparation, notary services, ITIN applications and renewals, sales tax reporting, and certified Spanish-English translations." } },
            { "@type": "Question", "name": "How do I schedule a consultation?", "acceptedAnswer": { "@type": "Answer", "text": "You can book an appointment online at romannumeralsiii.com/appointments, call 904-776-8318, or email info@romannumeralsiii.com." } }
          ]
        }}
      />
      {/* HERO */}
      <section className="relative overflow-hidden bg-[#FDFBF7]" data-testid="home-hero">
        <div className="rn3-container py-20 md:py-28 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 rn3-fade-up">
            <div className="rn3-eyebrow">Roman Numerals III</div>
            <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] mt-5 text-[#0A0A0A]">
              {t("home.hero.h1_a")}<br />
              <span className="italic text-[#8B1C22]">{t("home.hero.h1_b")}</span> {t("home.hero.h1_c")}
            </h1>
            <p className="mt-7 text-lg text-[#404040] max-w-xl leading-relaxed">
              {t("home.hero.sub")}
            </p>
            <div className="mt-9 flex flex-wrap gap-3" data-testid="hero-ctas">
              <Link to="/appointments" className="rn3-btn-primary" data-testid="cta-schedule">
                <Calendar className="w-4 h-4" /> {t("home.cta.schedule")}
              </Link>
              <Link to="/intake" className="rn3-btn-gold" data-testid="cta-intake">
                <FileText className="w-4 h-4" /> {t("home.cta.intake")}
              </Link>
              <Link to="/portal" className="rn3-btn-gold" data-testid="cta-upload">
                <Upload className="w-4 h-4" /> {t("home.cta.upload")}
              </Link>
              <Link to="/services" className="rn3-btn-gold" data-testid="cta-services">
                {t("home.cta.view_services")} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="mt-10 flex items-center gap-6 text-sm text-[#404040]">
              <div className="flex items-center gap-2"><Globe2 className="w-4 h-4 text-[#C4A252]" /> {t("home.badge.bilingual")}</div>
              <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#C4A252]" /> {t("home.badge.secure")}</div>
              <div className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#C4A252]" /> {t("home.badge.ai")}</div>
            </div>
          </div>

          <div className="lg:col-span-5 rn3-fade-up rn3-delay-2 flex justify-center lg:justify-end">
            <div className="relative">
              <div className="absolute -inset-5 bg-[#F3EFE9] -z-10" />
              <img
                src="https://plus.unsplash.com/premium_photo-1771963212984-f38fad47cd55?fm=jpg&q=75&w=1600&auto=format&fit=crop"
                alt="Diverse business team meeting in a modern boardroom"
                className="w-full max-w-[560px] aspect-[5/4] object-cover border-[6px] border-[#C4A252] shadow-xl"
                data-testid="hero-boardroom"
              />
              <img src={LOGO_URL} alt="" aria-hidden="true" loading="lazy" decoding="async" className="absolute -bottom-5 -left-5 w-20 h-20 bg-white p-2 border border-[#E5E5E5] shadow" />
            </div>
          </div>
        </div>

        <div className="rn3-container">
          <div className="border-t border-[#C4A252] w-24" />
        </div>
      </section>

      {/* SERVICE PREVIEW */}
      <section className="rn3-section bg-white" data-testid="home-services">
        <div className="rn3-container">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <div>
              <div className="rn3-eyebrow">{t("home.services.eyebrow")}</div>
              <h2 className="font-serif-display text-4xl md:text-5xl mt-3 text-[#0A0A0A]">
                {t("home.services.h2_a")} <span className="italic">{t("home.services.h2_b")}</span>
              </h2>
            </div>
            <Link to="/services" className="rn3-btn-gold self-start md:self-end" data-testid="view-all-services">
              {t("home.services.view_all")} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.map((s, i) => (
              <Link
                key={s.key}
                to={`/services#${s.key}`}
                className="rn3-card group"
                data-testid={`service-card-${s.key}`}
              >
                <div className="rn3-eyebrow text-[#C4A252]">0{i + 1}</div>
                <h3 className="font-serif-display text-2xl mt-3 text-[#0A0A0A] group-hover:text-[#8B1C22] transition-colors">
                  {t(`service.${s.key}`, s.title)}
                </h3>
                <div className="border-t border-[#C4A252] w-10 mt-4 mb-4" />
                <p className="text-sm text-[#404040] leading-relaxed">{t(`service.${s.key}.desc`, s.desc)}</p>
                <div className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-[#8B1C22]">
                  {t("home.services.learn_more")} <ArrowRight className="w-4 h-4" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* QR-CTA */}
      <section className="bg-[#0A0A0A] text-white" data-testid="home-qr-cta">
        <div className="rn3-container py-20 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="rn3-eyebrow text-[#C4A252]">{t("home.qr.eyebrow")}</div>
            <h2 className="font-serif-display text-4xl md:text-5xl mt-3">
              {t("home.qr.h2_a")} <span className="italic text-[#C4A252]">{t("home.qr.h2_b")}</span>
            </h2>
            <p className="mt-5 text-[#bdbdbd] max-w-md leading-relaxed">
              {t("home.qr.sub")}
            </p>
            <Link to="/intake" className="mt-8 inline-flex rn3-btn-gold border-[#C4A252] !text-white" data-testid="open-qr-welcome">
              {t("home.qr.open")} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="flex justify-center md:justify-end">
            <img
              src="/qr-intake.png"
              alt="Roman Numerals III — scan to start client intake"
              loading="lazy"
              className="w-64 md:w-80 h-auto bg-white border-4 border-[#C4A252] p-2"
              data-testid="home-qr-image"
            />
          </div>
        </div>
      </section>

      {/* Install-the-App section (PWA install prompts + iOS Add-to-Home-Screen instructions) */}
      <PwaInstall />

      {/* Social row */}
      <section className="border-t border-[#E5E5E5] bg-white">
        <div className="rn3-container py-10 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="text-center md:text-left">
            <div className="font-serif-display text-2xl text-[#0A0A0A]">{t("home.social.h2")}</div>
            <div className="text-sm text-[#737373]">{t("home.social.sub")}</div>
          </div>
          <SocialIcons />
        </div>
      </section>
    </div>
  );
};

export default Home;
